import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { MapPin, Navigation } from 'lucide-react';
import type { FlexHelper } from '@/lib/types';

interface LocationTrackerProps {
  helper: FlexHelper;
  onLocationUpdate: (location: GeolocationPosition) => void;
}

export function LocationTracker({ helper, onLocationUpdate }: LocationTrackerProps) {
  const [tracking, setTracking] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    let watchId: number;

    if (tracking && 'geolocation' in navigator) {
      watchId = navigator.geolocation.watchPosition(
        (position) => {
          onLocationUpdate(position);
          setError(undefined);
        },
        (err) => {
          setError(getLocationErrorMessage(err));
          setTracking(false);
        },
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0
        }
      );
    }

    return () => {
      if (watchId) {
        navigator.geolocation.clearWatch(watchId);
      }
    };
  }, [tracking, onLocationUpdate]);

  const getLocationErrorMessage = (error: GeolocationPositionError) => {
    switch (error.code) {
      case error.PERMISSION_DENIED:
        return "Location access denied. Please enable location services.";
      case error.POSITION_UNAVAILABLE:
        return "Location information unavailable.";
      case error.TIMEOUT:
        return "Location request timed out.";
      default:
        return "An unknown error occurred.";
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Location Tracking</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-primary" />
              <span className="font-medium">
                {tracking ? 'Currently Tracking' : 'Tracking Disabled'}
              </span>
            </div>
            <Button
              variant={tracking ? "destructive" : "default"}
              onClick={() => setTracking(!tracking)}
            >
              {tracking ? 'Log Out' : 'Log In'}
            </Button>
          </div>

          {helper.currentLocation && (
            <div className="rounded-lg border p-4 space-y-2">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Navigation className="h-4 w-4" />
                <span>Last known location</span>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-muted-foreground">Latitude</div>
                  <div className="font-medium">{helper.currentLocation.lat}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Longitude</div>
                  <div className="font-medium">{helper.currentLocation.lng}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Accuracy</div>
                  <div className="font-medium">±{Math.round(helper.currentLocation.accuracy)}m</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Last Update</div>
                  <div className="font-medium">
                    {new Date(helper.currentLocation.timestamp).toLocaleTimeString()}
                  </div>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="rounded-lg bg-destructive/10 text-destructive p-4 text-sm">
              {error}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}